/**
 * SaaSFoundation executive dashboard API client.
 * Base: /api/v1/executive/*
 */

import { getSaasApiBaseUrl } from '../utils/saasApiBaseUrl';

const getApiBaseUrl = getSaasApiBaseUrl;

function getAuthHeaders() {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatApiError(payload) {
  const d = payload?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ');
  }
  return payload?.message || 'Request failed';
}

const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;

async function apiRequest(path, options = {}) {
  const { timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${getApiBaseUrl()}${path}`, {
      ...fetchOptions,
      signal: controller.signal,
      headers: { ...getAuthHeaders(), ...(fetchOptions.headers || {}) },
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiError(payload));
    return payload;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Executive dashboard request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const executiveApiService = {
  async getConfig(organizationId, { includeHistory = true } = {}) {
    const data = await apiRequest(
      `/executive/config?organization_id=${encodeURIComponent(organizationId)}&include_history=${includeHistory}`
    );
    return {
      tiles: data.tiles || [],
      layout: data.layout || {},
      branding: data.branding || {},
      updatedAt: data.tiles_updated_at || data.layout_updated_at,
    };
  },

  async getCustomMetrics(organizationId, { includeHistory = true } = {}) {
    const data = await apiRequest(
      `/executive/custom-metrics?organization_id=${encodeURIComponent(organizationId)}&include_history=${includeHistory}`
    );
    return { tiles: data.tiles || [], updatedAt: data.updated_at };
  },

  async upsertCustomMetric(organizationId, tile) {
    return apiRequest('/executive/custom-metrics', {
      method: 'PUT',
      body: JSON.stringify({ organization_id: organizationId, tile }),
    });
  },

  async deleteCustomMetric(organizationId, tileId) {
    return apiRequest(
      `/executive/custom-metrics/${encodeURIComponent(tileId)}?organization_id=${encodeURIComponent(organizationId)}`,
      { method: 'DELETE' }
    );
  },

  async getLayout(organizationId) {
    const data = await apiRequest(
      `/executive/layout?organization_id=${encodeURIComponent(organizationId)}`
    );
    return {
      layout: data.layout || {},
      branding: data.branding || {},
    };
  },

  async saveLayout(organizationId, { layout, branding } = {}) {
    const payload = { organization_id: organizationId };
    if (layout != null) payload.layout = layout;
    if (branding != null) payload.branding = branding;
    const data = await apiRequest('/executive/layout', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return {
      layout: data.layout || {},
      branding: data.branding || {},
    };
  },

  async listFeeds(organizationId) {
    return apiRequest(`/executive/feeds?organization_id=${encodeURIComponent(organizationId)}`);
  },

  async createFeed(organizationId, feed) {
    return apiRequest('/executive/feeds', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, ...feed }),
    });
  },

  async updateFeed(organizationId, feedId, patch) {
    return apiRequest(
      `/executive/feeds/${encodeURIComponent(feedId)}?organization_id=${encodeURIComponent(organizationId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) }
    );
  },

  async deleteFeed(organizationId, feedId) {
    return apiRequest(
      `/executive/feeds/${encodeURIComponent(feedId)}?organization_id=${encodeURIComponent(organizationId)}`,
      { method: 'DELETE' }
    );
  },

  async syncFeed(organizationId, feedId, { csvText } = {}) {
    return apiRequest(
      `/executive/feeds/${encodeURIComponent(feedId)}/sync?organization_id=${encodeURIComponent(organizationId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ csv_text: csvText || null }),
      }
    );
  },

  async downloadSpreadsheetTemplate(organizationId) {
    const res = await fetch(
      `${getApiBaseUrl()}/executive/spreadsheet-template?organization_id=${encodeURIComponent(organizationId)}`,
      { headers: getAuthHeaders() }
    );
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(formatApiError(payload));
    }
    return res.text();
  },

  async importSpreadsheet(organizationId, { csvText, defaultAsOfDate } = {}) {
    return apiRequest('/executive/spreadsheet-import', {
      method: 'POST',
      body: JSON.stringify({
        organization_id: organizationId,
        csv_text: csvText,
        default_as_of_date: defaultAsOfDate || null,
      }),
    });
  },

  async getCustomMetricsHistory(organizationId, { externalId, limit = 90 } = {}) {
    const params = new URLSearchParams({ organization_id: organizationId, limit: String(limit) });
    if (externalId) params.set('external_id', externalId);
    return apiRequest(`/executive/custom-metrics/history?${params}`);
  },

  async listReports(organizationId, { limit = 50 } = {}) {
    const params = new URLSearchParams({
      organization_id: organizationId,
      limit: String(limit),
    });
    return apiRequest(`/executive/reports?${params}`);
  },

  async saveReport(organizationId, body) {
    return apiRequest('/executive/reports', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, ...body }),
    });
  },

  async getReport(organizationId, reportId) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return apiRequest(`/executive/reports/${encodeURIComponent(reportId)}?${params}`);
  },

  async deleteReport(organizationId, reportId) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return apiRequest(`/executive/reports/${encodeURIComponent(reportId)}?${params}`, {
      method: 'DELETE',
    });
  },

  async compareReports(organizationId, { baselineReportId, comparisonReportId }) {
    return apiRequest('/executive/reports/compare', {
      method: 'POST',
      body: JSON.stringify({
        organization_id: organizationId,
        baseline_report_id: baselineReportId,
        comparison_report_id: comparisonReportId,
      }),
    });
  },
};
